import React from 'react';
import {makeStyles} from '@mui/styles';
import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormattedAmount from '../ui-components/FormattedAmount';
import FormattedRate from '../ui-components/FormattedRate';
import {useSelector} from 'react-redux';
import BglLoader from '../ui-components/BglLoader';
import MomentUtils from '@date-io/moment';

const useStyles = makeStyles(theme => ({
    container: {
        backgroundColor: 'white',
        marginTop: '50px',
        padding: '10% 5%',
        [theme.breakpoints.up('md')]: {
            padding: '10% 15%'
        }
    },
    line: {
        borderBottom: '1px solid #C7C7C7',
        padding: '20px 0'
    }
}));

const Details = () => {
    const {t} = useTranslation(['fees-simulator']);
    const classes = useStyles();
    const momentUtils = new MomentUtils();

    const isFetching = useSelector(reducer => reducer.appState.get('isFetching'));
    const error = useSelector(reducer => reducer.appState.get('error'));
    const simulation = useSelector(reducer => reducer.appState.get('simulation'));
    const {operationDate, paymentAmount, convertedAmount, exchangeRate, bankCharges, ratio} = simulation;

    const paymentCurrencyResult = useSelector(reducer => reducer.appState.get('paymentCurrencyResult'));
    const changed = useSelector(reducer => reducer.appState.get('changed'));

    return (
        <div className={classes.container}>
            {isFetching ?
                <BglLoader/>
                :
                <>
                    {error &&
                        <Grid container spacing={6} justify='flex-start' alignItems='baseline'>
                            <Grid item xs={12}>
                                <Typography variant='h5'>{t('error.service')}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant='h6'>{t('error.later')}</Typography>
                            </Grid>
                        </Grid>
                    }

                    {!error && !changed &&
                        <Grid container spacing={6} justify='flex-start' alignItems='baseline'>
                            <Grid item xs={12}>
                                <Typography variant='h5'>{t('details.title')}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={classes.line} spacing={1}
                                        justify='flex-start' alignItems='baseline'>
                                    <Grid item xs={7}>
                                        <Typography variant='h6' align='left'>{t('panel.operation-date')}</Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant='h6' align='right'>{momentUtils.moment(operationDate).format('DD/MM/YYYY')}</Typography>
                                    </Grid>
                                </Grid>

                                <Grid container className={classes.line} spacing={1}
                                        justify='flex-start' alignItems='baseline'>
                                    <Grid item xs={7}>
                                        <Typography variant='h6' align='left'>{t('details.amount-payment')}</Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant='h6' align='right'>
                                            <FormattedAmount
                                                displayType='text'
                                                value={paymentAmount}
                                                suffix={' ' + paymentCurrencyResult}/>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container className={classes.line} spacing={1}
                                        justify='flex-start' alignItems='baseline'>
                                    <Grid item xs={7}>
                                        <Typography variant='h6' align='left'>{t('details.amount-converted')}</Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant='h6' align='right'>
                                            <FormattedAmount
                                                displayType='text'
                                                value={convertedAmount}
                                                suffix=' EUR'/>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container className={classes.line} spacing={1}
                                        justify='flex-start' alignItems='baseline'>
                                    <Grid item xs={7}>
                                        <Typography variant='h6' align='left'>{t('details.exchange-rate')} <sup>(1)</sup></Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant='h6' align='right'>
                                            1 {paymentCurrencyResult} = <FormattedAmount displayType='text' value={exchangeRate} decimalScale={4} suffix=' EUR'/>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container className={classes.line} spacing={1}
                                        justify='flex-start' alignItems='baseline'>
                                    <Grid item xs={7}>
                                        <Typography variant='h6' align='left'>{t('details.bank-fees')} <sup>(1)</sup></Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant='h6' align='right'>
                                            <FormattedAmount
                                                displayType='text'
                                                value={bankCharges}
                                                suffix=' EUR'/>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container className={classes.line} spacing={1}
                                        justify='flex-start' alignItems='baseline'>
                                    <Grid item xs={7}>
                                        <Typography variant='h6' align='left'>{t('details.ratio')} <sup>(2)</sup></Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant='h6' align='right'>
                                            <FormattedRate rate={ratio}/>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography paragraph variant='caption' gutterBottom><sup>(1)</sup> {t('details.fees-detail')}</Typography>
                                <Typography paragraph variant='caption' gutterBottom><sup>(2)</sup> {t('details.ratio-detail')}</Typography>
                            </Grid>
                        </Grid>
                    }
                </>

            }
        </div>
    );
};

export default React.memo(Details);
